import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Combobox, DateInput } from '../../../components/common';
import { apiError } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import {
  expenseIncomeReportService,
  expenseTypeService,
  incomeTypeService,
  bankAccountService,
} from '../../../services/resources';
import { sortByLabel } from '../../../config/columns';

const fmt = (n) =>
  n === null || n === undefined || n === '' ? '' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 });

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => today().slice(0, 8) + '01';

const detailColumns = [
  { key: 'date', label: 'Date', render: (r) => (r.date instanceof Date ? r.date : String(r.date).slice(0, 10)) },
  { key: 'doc_no', label: 'Doc No' },
  { key: 'type_name', label: 'Type' },
  { key: 'description', label: 'Description' },
  { key: 'bank_name', label: 'Bank' },
  { key: 'amount', label: 'Amount', render: (r) => fmt(r.amount), align: 'right' },
];

export default function ExpenseIncomeReportPage() {
  const { can } = useAuth();
  const canView = can('REPORT_EXPENSE_INCOME', 'CanView');

  const [dateFrom, setDateFrom] = useState(firstOfMonth());
  const [dateTo, setDateTo] = useState(today());
  const [expenseTypeId, setExpenseTypeId] = useState('');
  const [incomeTypeId, setIncomeTypeId] = useState('');
  const [bankId, setBankId] = useState('');

  const [expenseTypes, setExpenseTypes] = useState([]);
  const [incomeTypes, setIncomeTypes] = useState([]);
  const [banks, setBanks] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    expenseTypeService.list().then(setExpenseTypes).catch(() => {});
    incomeTypeService.list().then(setIncomeTypes).catch(() => {});
    bankAccountService.list().then(setBanks).catch(() => {});
  }, []);

  const filters = {
    dateFrom,
    dateTo,
    expense_type_id: expenseTypeId,
    income_type_id: incomeTypeId,
    bank_id: bankId,
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setData(await expenseIncomeReportService.list(filters));
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) load();
    // eslint-disable-next-line
  }, []);

  const doExport = async () => {
    setExporting(true);
    try {
      await expenseIncomeReportService.export(filters);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setExporting(false);
    }
  };

  if (!canView) {
    return (
      <Card title="Expense/Income Report">
        <p className="text-sm text-gray-500">You do not have permission to view this page.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card title="Expense/Income Report">
        <div className="flex flex-wrap items-end gap-3">
          <DateInput label="From" value={dateFrom} onChange={setDateFrom} />
          <DateInput label="To" value={dateTo} onChange={setDateTo} />
          <div className="w-52">
            <Combobox
              label="Expense Type"
              value={expenseTypeId}
              onChange={setExpenseTypeId}
              options={[{ value: '', label: 'All' }, ...sortByLabel(expenseTypes.map((t) => ({ value: t.id, label: t.name })))]}
            />
          </div>
          <div className="w-52">
            <Combobox
              label="Income Type"
              value={incomeTypeId}
              onChange={setIncomeTypeId}
              options={[{ value: '', label: 'All' }, ...sortByLabel(incomeTypes.map((t) => ({ value: t.id, label: t.name })))]}
            />
          </div>
          <div className="w-52">
            <Combobox
              label="Bank"
              value={bankId}
              onChange={setBankId}
              options={[{ value: '', label: 'All' }, ...sortByLabel(banks.map((b) => ({ value: b.id, label: b.bank_name })))]}
            />
          </div>
          <Button onClick={load} disabled={loading}>
            {loading ? 'Loading…' : 'Apply'}
          </Button>
          <Button variant="secondary" onClick={doExport} disabled={exporting}>
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </Button>
        </div>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      </Card>

      {data && (
        <>
          <Card title="Expense">
            <div className="max-h-72 overflow-y-auto">
              <Table columns={detailColumns} rows={data.expense.rows} loading={loading} />
            </div>
            <div className="mt-2 flex justify-end text-sm font-semibold text-gray-800">
              Total: {fmt(data.expense.total)}
            </div>
          </Card>

          <Card title="Income">
            <div className="max-h-72 overflow-y-auto">
              <Table columns={detailColumns} rows={data.income.rows} loading={loading} />
            </div>
            <div className="mt-2 flex justify-end text-sm font-semibold text-gray-800">
              Total: {fmt(data.income.total)}
            </div>
          </Card>

          <Card title="Summary">
            <div className="grid gap-1 text-sm text-gray-700 sm:grid-cols-2">
              {data.expense.summary.map((s) => (
                <div key={`e-${s.type_name}`} className="flex justify-between">
                  <span>Expense: {s.type_name}</span>
                  <span>{fmt(s.total)}</span>
                </div>
              ))}
              {data.income.summary.map((s) => (
                <div key={`i-${s.type_name}`} className="flex justify-between">
                  <span>Income: {s.type_name}</span>
                  <span>{fmt(s.total)}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 border-t border-gray-100 pt-3 text-sm font-semibold text-gray-800">
              Net (Income − Expense): {fmt(data.net)}
            </div>
            <p className="mt-1 text-xs text-gray-400">
              This is not gross profit — Phase 5b's P&amp;L report accounts for COGS separately.
            </p>
          </Card>
        </>
      )}
    </div>
  );
}
